import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Users, UserPlus, Loader2, Mail, Copy, Check, Trash2, Crown, Clock, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantTeam = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [practice, setPractice] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [changingRole, setChangingRole] = useState(null);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const [teamRes, practiceRes] = await Promise.all([
        axios.get(`${API_URL}/api/accountant/team`),
        axios.get(`${API_URL}/api/accountant/practice`)
      ]);
      setTeam(teamRes.data.team || []);
      setPractice(practiceRes.data);
    } catch (err) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await axios.post(`${API_URL}/api/accountant/team/invite`, {
        email: inviteEmail.trim(),
        name: inviteName.trim()
      });
      toast.success(`Invite created for ${inviteEmail}`);
      setShowInvite(false);
      setInviteEmail('');
      setInviteName('');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the practice?`)) return;
    try {
      await axios.delete(`${API_URL}/api/accountant/team/${memberId}`);
      toast.success('Team member removed');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleRoleChange = async (memberId, newRole, memberName) => {
    const label = newRole === 'practice_admin' ? 'Admin' : 'Team Member';
    if (!window.confirm(`Change ${memberName}'s role to ${label}?`)) return;
    setChangingRole(memberId);
    try {
      await axios.put(`${API_URL}/api/accountant/team/${memberId}/role`, { role: newRole });
      toast.success(`${memberName} is now ${label}`);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    } finally {
      setChangingRole(null);
    }
  };

  const isPracticeAdmin = practice?.is_practice_admin;

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="h-6 w-6 animate-spin text-sky-400" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Team Management</h1>
          <p className="text-slate-400 text-sm mt-1">{practice?.firm_name} — {team.length} member{team.length !== 1 ? 's' : ''}</p>
        </div>
        {isPracticeAdmin && (
          <Button onClick={() => setShowInvite(!showInvite)}
            className="bg-sky-600 hover:bg-sky-700 h-9 text-sm" data-testid="invite-member-btn">
            <UserPlus className="h-4 w-4 mr-2" />Invite
          </Button>
        )}
      </div>

      {/* Invite Form */}
      {showInvite && (
        <Card className="bg-slate-800 border-sky-500/30">
          <CardContent className="p-4">
            <form onSubmit={handleInvite} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-slate-300 text-xs">Name</Label>
                  <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Jane Doe" className="bg-slate-900 border-slate-600 text-white h-9 text-sm"
                    required data-testid="invite-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-300 text-xs">Email</Label>
                  <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="jane@practice.com" className="bg-slate-900 border-slate-600 text-white h-9 text-sm"
                    required data-testid="invite-email-input" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={inviting} className="bg-sky-600 hover:bg-sky-700 h-8 text-xs" data-testid="send-invite-btn">
                  {inviting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Send Invite
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowInvite(false)} className="h-8 text-xs text-slate-400">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Team List */}
      <div className="space-y-2">
        {team.map(member => (
          <Card key={member.id} className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    member.role === 'practice_admin' ? 'bg-amber-600/20' : 'bg-sky-600/20'
                  }`}>
                    {member.user ? (
                      <span className={`font-semibold text-sm ${member.role === 'practice_admin' ? 'text-amber-400' : 'text-sky-400'}`}>
                        {member.user.name?.charAt(0)?.toUpperCase()}
                      </span>
                    ) : (
                      <Clock className="h-4 w-4 text-slate-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white truncate">
                        {member.user?.name || member.invite_name || 'Pending'}
                      </p>
                      {member.role === 'practice_admin' && (
                        <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded-full">
                          <Crown className="h-2.5 w-2.5" />Admin
                        </span>
                      )}
                      {!member.user_id && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-slate-400 rounded-full">Pending invite</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{member.user?.email || member.invite_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!member.user_id && member.invite_code && (
                    <Button variant="ghost" size="sm" onClick={() => copyCode(member.invite_code)}
                      className="h-7 px-2 text-xs text-slate-400" data-testid={`copy-code-${member.id}`}>
                      {copiedCode === member.invite_code ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                      Code
                    </Button>
                  )}
                  {isPracticeAdmin && member.user_id && member.role === 'team_member' && (
                    <Button variant="ghost" size="sm"
                      disabled={changingRole === member.id}
                      onClick={() => handleRoleChange(member.id, 'practice_admin', member.user?.name)}
                      className="h-7 px-2 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
                      title="Promote to Admin"
                      data-testid={`promote-${member.id}`}>
                      <ShieldCheck className="h-3 w-3 mr-1" />Promote
                    </Button>
                  )}
                  {isPracticeAdmin && member.user_id && member.role === 'practice_admin' && member.user_id !== user?.id && (
                    <Button variant="ghost" size="sm"
                      disabled={changingRole === member.id}
                      onClick={() => handleRoleChange(member.id, 'team_member', member.user?.name)}
                      className="h-7 px-2 text-xs text-orange-400 hover:text-orange-300 hover:bg-orange-400/10"
                      title="Demote to Team Member"
                      data-testid={`demote-${member.id}`}>
                      <ShieldOff className="h-3 w-3 mr-1" />Demote
                    </Button>
                  )}
                  {isPracticeAdmin && member.role !== 'practice_admin' && (
                    <Button variant="ghost" size="sm" onClick={() => handleRemove(member.id, member.user?.name || member.invite_name)}
                      className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-400/10" data-testid={`remove-${member.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AccountantTeam;
